import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, CameraOff, RefreshCw } from "lucide-react";
import { Btn } from "@/components/kit";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const focusModes = (track) => track?.getCapabilities?.().focusMode ?? [];
async function applyFocus(track, mode, point) {
  if (!focusModes(track).includes(mode)) return false;
  const adv = { focusMode: mode };
  if (point && track.getCapabilities().pointsOfInterest !== undefined) adv.pointsOfInterest = [point];
  try { await track.applyConstraints({ advanced: [adv] }); return true; } catch { return false; }
}

export default function CameraCapture({ onCapture, autoStart, onFail }) {
  const video = useRef(null);
  const stream = useRef(null);
  const ringTimer = useRef(null);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState(null);
  const [ring, setRing] = useState(null);
  const [autoFocus, setAutoFocus] = useState(false);
  const [shooting, setShooting] = useState(false);
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const track = () => stream.current?.getVideoTracks()[0];

  const stop = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  }, []);
  const start = useCallback(async () => {
    setErr(null);
    setState("starting");
    try {
      stop();
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 2560 }, height: { ideal: 1440 } }, audio: false });
      video.current.srcObject = stream.current;
      await video.current.play();
      setAutoFocus(await applyFocus(track(), "continuous"));
      setState("live");
    } catch (e) {
      const m = e?.name === "NotAllowedError" ? "Camera permission was denied. Allow camera access in your browser settings, or upload a photo instead." : "No camera available. Upload a photo of the card instead.";
      setErr(m);
      setState("error");
      onFail?.(m);
    }
  }, [stop, onFail]);
  useEffect(() => {
    if (!autoStart) return;
    if (supported) start();
    else onFail?.("Camera capture isn't supported in this browser. Please upload an image.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => { stop(); clearTimeout(ringTimer.current); }, [stop]);

  const tapFocus = async (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    setRing({ x: x * 100, y: y * 100, k: Date.now() });
    clearTimeout(ringTimer.current);
    ringTimer.current = setTimeout(() => setRing(null), 1100);
    const t = track();
    if (await applyFocus(t, "single-shot", { x, y })) setTimeout(() => applyFocus(t, "continuous"), 1500);
  };

  const grab = async () => {
    const t = track();
    if (window.ImageCapture && t) {
      try { return await new window.ImageCapture(t).takePhoto(); } catch {}
    }
    const v = video.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    return new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
  };
  const capture = async () => {
    setShooting(true);
    try {
      if (await applyFocus(track(), "single-shot")) await wait(450);
      else await wait(150);
      const b = await grab();
      if (b) onCapture(new File([b], `capture-${Date.now()}.jpg`, { type: "image/jpeg" }));
      applyFocus(track(), "continuous");
    } finally {
      setShooting(false);
    }
  };

  if (!supported) return <p className="text-[14px] text-[#64748B] p-6 text-center" data-testid="camera-unsupported">Camera capture isn't supported in this browser. Please upload an image.</p>;
  return (
    <div className="relative rounded-[20px] overflow-hidden bg-[#0F172A] aspect-[4/3]" data-testid="camera-capture">
      <video ref={video} playsInline muted onClick={state === "live" ? tapFocus : undefined} className={`w-full h-full object-cover cursor-crosshair ${state === "live" ? "" : "hidden"}`} data-testid="camera-video" />
      {state === "live" && (
        <>
          <div className="absolute inset-[16%_8%_24%_8%] border-2 border-white/90 rounded-[14px] shadow-[0_0_0_9999px_rgba(15,23,42,.45)] pointer-events-none" />
          {ring && <span key={ring.k} className="absolute w-16 h-16 -ml-8 -mt-8 rounded-full border-2 border-[#FACC15] pointer-events-none animate-[ping_0.9s_ease-out_1]" style={{ left: `${ring.x}%`, top: `${ring.y}%` }} data-testid="camera-focus-ring" />}
          <p className="absolute top-3 inset-x-0 text-center text-white text-[13px] font-medium pointer-events-none">Align the card inside the frame · tap the card to focus</p>
          <span className="absolute top-9 left-1/2 -translate-x-1/2 z-10 rounded-full bg-black/55 text-white text-[11px] font-semibold px-2.5 py-1 pointer-events-none" data-testid="camera-focus-mode">{shooting ? "Focusing…" : autoFocus ? "Auto-focus on" : "Auto-focus by device"}</span>
          <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-6">
            <button className="w-11 h-11 rounded-full bg-white/20 text-white flex items-center justify-center" onClick={start} title="Restart camera" data-testid="camera-restart"><RefreshCw className="w-5 h-5" /></button>
            <button className="w-[68px] h-[68px] rounded-full bg-white ring-4 ring-white/40 active:scale-95 transition-transform disabled:opacity-60 flex items-center justify-center" onClick={capture} disabled={shooting} data-testid="camera-shutter" aria-label="Capture">{shooting && <span className="w-6 h-6 rounded-full border-2 border-[#0000EC] border-t-transparent animate-spin" />}</button>
            <button className="w-11 h-11 rounded-full bg-white/20 text-white flex items-center justify-center" onClick={() => { stop(); setState("idle"); }} title="Stop camera" data-testid="camera-stop"><CameraOff className="w-5 h-5" /></button>
          </div>
        </>
      )}
      {state !== "live" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <Camera className="w-10 h-10 text-white/80" />
          {err && <p className="text-[14px] text-white/85 max-w-sm" data-testid="camera-error">{err}</p>}
          <Btn onClick={start} loading={state === "starting"} testid="camera-start">Start camera</Btn>
        </div>
      )}
    </div>
  );
}
